import {
  VacancyList,
  VacancyListFilters,
  type VacancyFiltersQuery,
} from '@features/vacancy';
import { useGetCandidateResume } from '@entities/candidates';
import { useGetCandidateVacancies } from '@entities/vacancy';
import { Anchor, Flex, Text } from '@mantine/core';
import { joinStringWithFallback } from '@shared/helpers';
import { useBreadcrumbs } from '@shared/lib';
import { PaginateType, type SortQuery } from '@shared/types';
import { PageContainer } from '@shared/ui';
import { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router';

export const CandidateResumeProposedVacanciesPage = () => {
  const { id = '', resumeId = '' } = useParams();
  const [filter, setFilter] = useState<VacancyFiltersQuery>({});
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    perPage: '15',
  });
  const [page, setPage] = useState(Number(searchParams.get('page')));
  const [pageSize, setPageSize] = useState(Number(searchParams.get('perPage')));

  const {
    data: resume,
    isLoading: isResumeLoading,
    isFetching: isResumeFetching,
  } = useGetCandidateResume(id, resumeId);

  const scopedFilter = useMemo(
    () => ({ ...filter, resumeId }),
    [filter, resumeId]
  );
  const listEnabled = Boolean(resume) && !isResumeLoading;

  const {
    data: vacancies,
    isLoading: isVacanciesLoading,
    isFetching: isVacanciesFetching,
  } = useGetCandidateVacancies(id, {
    filter: scopedFilter,
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
    enabled: listEnabled,
  });

  const { clearBreadcrumb, setBreadcrumb } = useBreadcrumbs();
  const isResumeAvailable = Boolean(resume);
  const resumeFirstName = resume?.firstName;
  const resumeLastName = resume?.lastName;
  const resumeDesiredJob = resume?.desiredJob;

  useEffect(() => {
    if (!isResumeAvailable) {
      return;
    }

    const candidateName = joinStringWithFallback(
      [resumeFirstName, resumeLastName],
      { fallback: '-' }
    );
    const resumeName = resumeDesiredJob?.trim() || 'Резюме';

    setBreadcrumb(
      <Flex gap={4} wrap='nowrap' align='center'>
        <Anchor to='/candidate-list' component={NavLink} variant='gray'>
          Кандидаты
        </Anchor>
        <Text>/</Text>
        <Anchor to={`/candidate-list/${id}`} component={NavLink} variant='gray'>
          {candidateName}
        </Anchor>
        <Text>/</Text>
        <Anchor
          to={`/candidate-list/${id}/resume/${resumeId}`}
          component={NavLink}
          variant='gray'
        >
          {resumeName}
        </Anchor>
        <Text>/</Text>
        <Text c='tertiary.4' truncate='end' maw='40dvw'>
          Предложенные вакансии
        </Text>
      </Flex>
    );

    return clearBreadcrumb;
  }, [
    clearBreadcrumb,
    id,
    isResumeAvailable,
    resumeDesiredJob,
    resumeFirstName,
    resumeLastName,
    resumeId,
    setBreadcrumb,
  ]);

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    setSort(direction ? { [field]: direction } : { id: 'desc' });
  };

  const isLoading =
    isResumeLoading ||
    isResumeFetching ||
    (listEnabled && (isVacanciesLoading || isVacanciesFetching));
  const totalRecords = listEnabled ? (vacancies?.pagination?.total ?? 0) : 0;

  return (
    <PageContainer title='Предложенные вакансии' canGoBack>
      <VacancyListFilters
        setFilter={data => {
          setFilter(data);
          setPage(1);
        }}
      />

      <VacancyList
        vacancies={listEnabled ? (vacancies?.data ?? []) : []}
        totalRecords={totalRecords}
        page={page}
        setPage={nextPage => {
          setPage(nextPage);
          setSearchParams({
            page: nextPage.toString(),
            perPage: pageSize.toString(),
          });
        }}
        pageSize={pageSize}
        setPageSize={size => {
          setPage(1);
          setPageSize(size);
          setSearchParams({ page: '1', perPage: size.toString() });
        }}
        isFilledFilters={Boolean(Object.keys(filter).length)}
        onSortChange={handleSortChange}
        currentSort={sort}
        isLoading={isLoading}
      />
    </PageContainer>
  );
};
